import * as React from 'react'
import { useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material'
import { useNotify, useRefresh } from 'react-admin'
import { client } from '../../providers/data/client'
import { useCouponPreflight } from './CouponPreflightProvider'

// Hover text explaining the button's colour. The codes come from the batch pre-flight;
// the full sentences the admin will actually be shown come from the single-kit call.
const WARNING_LABELS = {
  recentVasectomy: 'vasectomy was less than 12 weeks ago',
  priorCoupon: 'this customer has had a coupon before',
  providerGroupNoCoupons: 'sending replacement kit coupons is not enabled for this provider group',
}

const buttonTitle = (entry, loading) => {
  if (loading || !entry) {
    return 'Checking eligibility…'
  }
  if (entry.clean) {
    return 'All checks pass'
  }
  const reasons = (entry.warningCodes || []).map((code) => WARNING_LABELS[code] || code)
  return reasons.length ? `You will be asked to confirm: ${reasons.join('; ')}` : 'Eligibility could not be checked'
}

/**
 * "Create Coupon Code" — issues a 100%-off replacement kit coupon for a kit whose test
 * has finished (Test Complete, Manual Review, Notify Complete or Error).
 *
 * The flow is a queue, not a fixed sequence of dialogs: the server decides which of the
 * three warnings apply to this kit and returns them in order, and we pop one at a time.
 * Every warning must be answered Yes; a single No cancels the whole thing.
 *
 *   idle -> loading -> [warn]* -> confirm -> submitting -> idle
 *
 * There is no product picker: a replacement is always one MFHRK retail kit, and the
 * server resolves that variant itself rather than trusting anything we send.
 */

const CreateCouponButton = ({ record }) => {
  const [step, setStep] = useState('idle') // idle | loading | warn | confirm | submitting
  const [queue, setQueue] = useState([]) // warnings still to be acknowledged
  const [acknowledged, setAcknowledged] = useState([])
  const [preflight, setPreflight] = useState(null)
  // Why the coupon is being issued. Drives the paragraph the customer reads, so it is
  // required — there is no sensible default to fall back to.
  const [reasonCode, setReasonCode] = useState('')

  const notify = useNotify()
  const refresh = useRefresh()
  const { entry, loading: preflightLoading } = useCouponPreflight(record.kitId)

  const reset = () => {
    setStep('idle')
    setQueue([])
    setAcknowledged([])
    setPreflight(null)
    setReasonCode('')
  }

  const errorMessage = (err, fallback) => err?.response?.data?.message || err?.body?.message || err?.message || fallback

  const advance = (remaining, acked) => {
    setAcknowledged(acked)
    if (remaining.length === 0) {
      setQueue([])
      setStep('confirm')
    } else {
      setQueue(remaining)
      setStep('warn')
    }
  }

  const start = async (e) => {
    e.stopPropagation()
    setStep('loading')
    try {
      const res = await client().get(`/coupons/preflight?kitId=${record.kitId}`)
      const data = res.data
      setPreflight(data)

      if (data.alreadyIssued) {
        notify(`This kit already has a replacement coupon (${data.existingCouponCode}).`, {
          type: 'warning',
          multiLine: true,
        })
        reset()
        return
      }

      advance(data.warnings || [], [])
    } catch (err) {
      console.error('Coupon preflight error:', err)
      notify(errorMessage(err, 'Could not check this kit for coupon eligibility.'), { type: 'error' })
      reset()
    }
  }

  const acceptWarning = () => {
    const [current, ...rest] = queue
    advance(rest, [...acknowledged, current.code])
  }

  const submit = async () => {
    setStep('submitting')
    try {
      const res = await client().post('/coupons', {
        kitId: record.kitId,
        acknowledgedWarnings: acknowledged,
        reasonCode,
      })
      const data = res.data

      // A coupon whose email failed is still a real, usable code — surface it so staff
      // can pass it on by hand rather than issuing a second one.
      notify(data.message, { type: data.emailSent ? 'success' : 'warning', multiLine: true })
      reset()
      refresh()
    } catch (err) {
      console.error('Create coupon error:', err)
      notify(errorMessage(err, 'Failed to create the coupon.'), { type: 'error', multiLine: true })
      setStep('confirm')
    }
  }

  const busy = step === 'loading' || step === 'submitting'
  const currentWarning = queue[0]
  // Served by the pre-flight rather than hardcoded here, so the labels stay tied to the
  // customer-facing copy the server pairs with each code.
  const reasonChoices = preflight?.defaults?.reasonCodes || []
  // Display only. The server resolves the variant from this same env-backed SKU, so the
  // dialog cannot promise a product the coupon will not actually be issued against.
  const sku = preflight?.defaults?.sku || 'MFHRK'

  // Blue when the page-level pre-flight found nothing to warn about, grey otherwise.
  // Grey is still clickable — it means "you will be asked to confirm", not "blocked" —
  // and it is also what we show while the pre-flight is in flight or if it failed, so
  // the button never promises a clean run it hasn't verified.
  const clean = !!(entry && entry.clean)
  const buttonClass = clean ? 'btn btn-primary text-white' : 'btn btn-secondary text-black'

  return (
    <>
      <button className={buttonClass} onClick={start} disabled={busy} title={buttonTitle(entry, preflightLoading)}>
        <span className="fa-solid fa-ticket"></span>&nbsp;
        {step === 'loading' ? 'Checking…' : 'Create Coupon Code'}
      </button>

      <Dialog open={step === 'warn'} onClose={reset} maxWidth="xs" fullWidth>
        <DialogTitle>Create Coupon Code</DialogTitle>
        <DialogContent>
          <DialogContentText>{currentWarning ? currentWarning.message : ''}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={reset}>No</Button>
          <Button onClick={acceptWarning} color="primary" variant="contained">
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={step === 'confirm' || step === 'submitting'} onClose={busy ? undefined : reset} maxWidth="sm" fullWidth>
        <DialogTitle>Create Coupon Code</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Customer:</strong> {preflight?.patientName || '—'}
              </Typography>
              <Typography variant="body2">
                <strong>Email:</strong> {preflight?.patientEmail || '—'}
              </Typography>
              <Typography variant="body2">
                <strong>Kit:</strong> {record.kitId}
              </Typography>
              <Typography variant="body2">
                <strong>Product:</strong> {sku}
              </Typography>
              <Typography variant="body2">
                <strong>Valid through:</strong> {preflight?.defaults?.expiresOn || '—'}
              </Typography>
            </Box>
          </DialogContentText>

          <FormControl fullWidth required size="small" sx={{ mt: 1 }}>
            <InputLabel id="coupon-reason-label">Reason</InputLabel>
            <Select
              labelId="coupon-reason-label"
              value={reasonCode}
              label="Reason"
              disabled={busy}
              onChange={(e) => setReasonCode(e.target.value)}
            >
              {reasonChoices.map((choice) => (
                <MenuItem key={choice.id} value={choice.id}>
                  {choice.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            The customer will receive a code for 100% off one {sku} kit, valid only for their email address.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={reset} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            color="primary"
            variant="contained"
            disabled={busy || !reasonCode}
            startIcon={step === 'submitting' ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {step === 'submitting' ? 'Creating…' : 'Create & Send'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default CreateCouponButton
